import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import type { SkillShort, SkillsDetectionRequest } from '~/shared/data-contract/skill';
import { useLearningOpportunitiesCustomAttributes } from '~/shared/query-hooks/learning-opportunities';
import { ConfirmationDialog } from '~/ui/core/confirmation-dialog/ConfirmationDialog';
import { TileStickyHeader } from '~/ui/core/tile/Tile';
import { SidepanelTitle } from '~/ui/domain/Sidepanel/components/SidepanelTitle';

import { useSkillsDetectionQuery } from '../../app/query-hooks/skills';
import {
    ContentContainer,
    ContentSections,
    FlexRowContainer,
    useDiscardConfirmation,
    Wrapper,
} from '../../job-architecture-v2';
import { useUrlTileEscapeClose } from '../../url-tiles';
import { useFeaturedOpportunityOptionsQuery, useLearningOpportunityPermissions } from '../api';
import type { LearningOpportunity, LearningOpportunityCreate } from '../data-contract';
import {
    findInvalidTitle,
    getDisplayedTitle,
    getLocalizedTranslationIndex,
    getOriginLanguages,
    getPickerLanguages,
    getTextLanguages,
    isTranslationTitleValid,
    type LearningOpportunityFormState,
    submitFormPayload,
    TITLE_MAX_LENGTH,
    TITLE_MIN_LENGTH,
} from '../form-state';
import { type EditableLearningOpportunityState, learningLanguageCodes } from '../helpers';
import { useAutoAddDetectedSkills } from '../hooks/use-auto-add-detected-skills';
import { CourseActionBar, CourseActionBarContent } from './CourseActionBar';
import { CourseMetadataRow } from './CourseMetadataRow';
import { CourseTypeRow } from './CourseTypeRow';
import { DescriptionSection } from './DescriptionSection';
import { LearningOpportunityActions } from './LearningOpportunityActions';
import { LearningOpportunityPublishNotification } from './LearningOpportunityPublishNotification';
import { SidepanelHeaderNavigation, SidepanelHeaderTitle } from './SidepanelHeader';
import { SkillsColumn } from './SkillsColumn';
import { TranslationOriginHint } from './TranslationOriginHint';
import { UrlSection } from './UrlSection';

const translateTitleError = (translate: ReturnType<typeof useTranslate>, title: string): string => {
    const field = translate('pages.learning-opportunities.title');
    if (title.length === 0) return translate('validation.required', { field });
    if (title.length < TITLE_MIN_LENGTH) return translate('validation.minLength', { field, min: TITLE_MIN_LENGTH });

    return translate('validation.maxLength', { field, max: TITLE_MAX_LENGTH });
};

const validateTitle = (
    translate: ReturnType<typeof useTranslate>,
    translations: LearningOpportunityFormState['translations'],
    index: number,
    title: string,
): true | string => isTranslationTitleValid(translations, index) || translateTitleError(translate, title);

const TitleErrorMessage: FunctionComponent<{ readonly isEditing: boolean; readonly message?: string }> = ({
    isEditing,
    message,
}) =>
    isEditing && message !== undefined ? (
        <p className="m-0 text-body-s text-text-error" role="alert">
            {message}
        </p>
    ) : null;

export const LearningOpportunityContent: FunctionComponent<{
    readonly defaultValue: LearningOpportunityFormState;
    readonly isPublishing?: boolean;
    readonly isSubmitting?: boolean;
    readonly learningOpportunity?: LearningOpportunity;
    readonly mode: 'create' | 'edit' | 'view';
    readonly onCancel: () => void;
    readonly onClose: () => void;
    readonly onEdit?: () => void;
    readonly onPublish?: () => void;
    readonly onSkillClick?: (skillId: string, preferredLanguage?: string) => void;
    readonly onTrainedSkillsChange?: (skills: readonly SkillShort[]) => void;
    readonly onSubmit: (value: LearningOpportunityCreate) => void;
}> = ({
    defaultValue,
    isPublishing = false,
    isSubmitting = false,
    learningOpportunity,
    mode,
    onCancel,
    onClose,
    onEdit,
    onPublish,
    onSkillClick,
    onSubmit,
    onTrainedSkillsChange,
}) => {
    const isEditing = mode === 'create' || mode === 'edit';
    const translate = useTranslate();
    const {
        applicationState: { availableLanguages, customization, language },
    } = useApplicationContext();
    const featuredOptions = useFeaturedOpportunityOptionsQuery(learningOpportunity?.id ?? '');
    const { canUserEditLearningOpportunity, isPending: isPermissionsPending } = useLearningOpportunityPermissions(
        learningOpportunity?.id ?? '',
    );
    const customAttributes = useLearningOpportunitiesCustomAttributes();
    const providerOptions = customization
        .get('learningOpportunitiesConfiguration')
        .get('learningOpportunityProviders')
        .unwrapOr([] as string[]);
    const skillLimit = customization
        .get('learningOpportunitiesConfiguration')
        .get('learningOpportunitySkillCountLimit')
        .unwrapOr(50);
    const languageCodes = Object.values(availableLanguages).map((item) => item.code);
    const textLanguages = getTextLanguages(defaultValue.translations);
    const pickerLanguages = getPickerLanguages(languageCodes, textLanguages);
    const [activeLanguage, setActiveLanguage] = useState(
        languageCodes.includes(language.code)
            ? language.code
            : (defaultValue.translations[0]?.language ?? language.code),
    );
    const [blacklistedDetectedSkillIds, setBlacklistedDetectedSkillIds] = useState<readonly string[]>([]);
    const methods = useForm<LearningOpportunityFormState>({
        defaultValues: defaultValue,
        mode: 'onChange',
    });
    const values = methods.watch();
    const trainedSkills = methods.watch('skills') ?? [];
    const translationIndex = getLocalizedTranslationIndex(values, activeLanguage);
    const activeTranslation = values.translations[translationIndex];
    const originLanguages = getOriginLanguages(textLanguages, activeLanguage);
    const detectionPayload = useMemo<SkillsDetectionRequest>(
        () => ({
            language: activeLanguage,
            text: activeTranslation?.description ?? '',
            title: activeTranslation?.title ?? '',
        }),
        [activeLanguage, activeTranslation?.description, activeTranslation?.title],
    );
    const skillsDetectionQuery = useSkillsDetectionQuery('course', detectionPayload, mode === 'create', {
        includeProficienciesSearchParam: false,
    });
    useAutoAddDetectedSkills({
        blacklistedSkillIds: blacklistedDetectedSkillIds,
        detectedSkills: skillsDetectionQuery.data ?? [],
        methods,
        mode,
        skillLimit,
    });

    useEffect(() => {
        onTrainedSkillsChange?.(trainedSkills);
    }, [onTrainedSkillsChange, trainedSkills]);

    const handleSkillRemoved = (skillId: string): void => {
        if (mode !== 'create') return;
        setBlacklistedDetectedSkillIds((current) => (current.includes(skillId) ? current : [...current, skillId]));
    };
    const handleStateChange = (next: EditableLearningOpportunityState): void => {
        methods.setValue('state', next, { shouldDirty: true, shouldValidate: true });
    };
    // Every sent translation is validated; the panel switches to the first language with an invalid title.
    const showInvalidTitle = (translations: LearningOpportunityFormState['translations']): boolean => {
        const invalid = findInvalidTitle(translations);
        if (invalid === undefined) return false;

        methods.setError(`translations.${invalid.index}.title`, {
            message: translateTitleError(translate, invalid.translation.title),
            type: 'validate',
        });
        setActiveLanguage(invalid.translation.language);
        return true;
    };
    const submit = (value: LearningOpportunityFormState): void => {
        if (showInvalidTitle(value.translations)) return;
        submitFormPayload(value, onSubmit);
    };
    const titleError = methods.formState.errors.translations?.[translationIndex]?.title?.message;

    const {
        handleCancel: handleDiscardCancel,
        handleCancelDiscard,
        handleClose: handleDiscardClose,
        handleConfirmDiscard,
        isDiscardDialogOpen,
    } = useDiscardConfirmation({
        formMethods: methods,
        isEditing,
        onClose,
        setIsEditing: (editing) => {
            if (!editing) onCancel();
        },
    });

    // Escape at the tile root routes through the discard confirmation instead
    // of silently closing over unsaved edits.
    useUrlTileEscapeClose(handleDiscardClose);

    return (
        <FormProvider {...methods}>
            <ConfirmationDialog
                cancelButtonLabel={translate('common.cancel')}
                confirmButtonLabel={translate('common.confirm')}
                confirmButtonVariant="default"
                description={translate('dialogs.discard.content')}
                open={isDiscardDialogOpen}
                title={translate('dialogs.discard.title')}
                onCancel={handleCancelDiscard}
                onConfirm={handleConfirmDiscard}
            />
            <form
                onSubmit={methods.handleSubmit(submit, () => showInvalidTitle(methods.getValues('translations')))}
                className="w-full"
            >
                <Wrapper>
                    <TileStickyHeader>
                        <SidepanelHeaderNavigation
                            activeLanguage={activeLanguage}
                            actions={
                                learningOpportunity === undefined ? undefined : (
                                    <LearningOpportunityActions
                                        canManageFeaturedLearningOpportunities={
                                            featuredOptions.data?.canManageFeaturedLearningOpportunities === true
                                        }
                                        learningOpportunity={learningOpportunity}
                                    />
                                )
                            }
                            availableLanguages={pickerLanguages}
                            textLanguages={textLanguages}
                            onClose={handleDiscardClose}
                            onLanguageChange={setActiveLanguage}
                        />
                    </TileStickyHeader>
                    {learningOpportunity !== undefined &&
                        learningOpportunity.state === 'draft' &&
                        canUserEditLearningOpportunity &&
                        onPublish !== undefined && (
                            <LearningOpportunityPublishNotification
                                disabled={isPublishing || isEditing}
                                onPublish={onPublish}
                            />
                        )}
                    <SidepanelHeaderTitle>
                        <CourseTypeRow />
                        <SidepanelTitle
                            key={translationIndex}
                            isEditing={isEditing}
                            placeholder={translate('pages.learning-opportunities.title-placeholder')}
                            register={methods.register(`translations.${translationIndex}.title`, {
                                validate: (title, formValues) =>
                                    validateTitle(translate, formValues.translations, translationIndex, title),
                            })}
                            title={getDisplayedTitle(activeTranslation?.title, learningOpportunity?.title, isEditing)}
                        />
                        <TitleErrorMessage isEditing={isEditing} message={titleError} />
                        {isEditing && learningOpportunity !== undefined && is.nonEmptyArray(originLanguages) && (
                            <TranslationOriginHint languages={originLanguages} onSelectLanguage={setActiveLanguage} />
                        )}
                    </SidepanelHeaderTitle>
                    <ContentContainer>
                        {learningOpportunity !== undefined && (
                            <CourseMetadataRow
                                isEditing={isEditing}
                                learningLanguage={learningOpportunity.learningLanguage}
                                onStateChange={handleStateChange}
                                ownerName={learningOpportunity.ownerName}
                                provider={learningOpportunity.provider}
                                state={isEditing ? values.state : learningOpportunity.state}
                                updatedAt={learningOpportunity.updatedAt}
                            />
                        )}
                        <CourseActionBar>
                            <CourseActionBarContent
                                canUserEditLearningOpportunity={canUserEditLearningOpportunity}
                                isEditing={isEditing}
                                isPermissionsPending={isPermissionsPending}
                                isSubmitting={isSubmitting}
                                learningOpportunity={learningOpportunity}
                                onCancel={handleDiscardCancel}
                                onEdit={onEdit}
                            />
                        </CourseActionBar>
                        <FlexRowContainer>
                            <ContentSections data-testid="learning-opportunity-content">
                                <div className="flex flex-col gap-xl">
                                    <DescriptionSection
                                        fallbackHtml={learningOpportunity?.description}
                                        isEditing={isEditing}
                                        translationIndex={translationIndex}
                                    />
                                    <section
                                        className="flex flex-col gap-m"
                                        data-testid="learning-opportunity-url-section"
                                    >
                                        <UrlSection
                                            customAttributes={customAttributes}
                                            isEditing={isEditing}
                                            learningLanguageCodes={learningLanguageCodes}
                                            providerOptions={providerOptions}
                                            url={values.url}
                                        />
                                    </section>
                                </div>
                                <div className="flex flex-col gap-xl">
                                    <SkillsColumn
                                        isDetectionPending={mode === 'create' && skillsDetectionQuery.isFetching}
                                        isEditing={isEditing}
                                        languageCode={activeLanguage}
                                        skills={values.skills ?? []}
                                        onSkillClick={onSkillClick}
                                        onSkillRemoved={handleSkillRemoved}
                                    />
                                </div>
                            </ContentSections>
                        </FlexRowContainer>
                    </ContentContainer>
                </Wrapper>
            </form>
        </FormProvider>
    );
};
